/**
 * ProjectEditModal - Reusable modal for editing project metadata
 * Used by ProjectsPage and ProjectDetailContent
 */

import { useState } from 'react';
import { projectsApi } from '../services/api';
import type { ProjectInstance } from '../types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  Button,
  Input,
  Select,
  Label,
  Spinner,
} from '@/components/ui';

interface ProjectEditModalProps {
  project: ProjectInstance;
  onClose: () => void;
  onSaved: (updatedProject: ProjectInstance) => void;
  open?: boolean;
}

export function ProjectEditModal({ project, onClose, onSaved, open = true }: ProjectEditModalProps) {
  const [name, setName] = useState(project.name || '');
  const [projectCode, setProjectCode] = useState(project.projectCode || '');
  const [projectTitle, setProjectTitle] = useState(project.projectTitle || '');
  const [customer, setCustomer] = useState(project.customer || '');
  const [program, setProgram] = useState(project.program || '');
  const [certAuthority, setCertAuthority] = useState(project.certificationAuthority || '');
  const [certBasis, setCertBasis] = useState<string[]>(project.certificationBasis || []);
  const [codeCheckMessage, setCodeCheckMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasDocuments = (project.documents?.length || 0) > 0;
  const canChangeCode = !project.projectCode || !hasDocuments;

  // Check project code availability
  const handleProjectCodeChange = async (code: string) => {
    const upperCode = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setProjectCode(upperCode);

    if (upperCode.length < 3) {
      setCodeCheckMessage('');
      return;
    }

    if (upperCode === project.projectCode) {
      setCodeCheckMessage('');
      return;
    }

    try {
      const isAvailable = await projectsApi.checkCodeUnique(upperCode);
      setCodeCheckMessage(isAvailable ? '✓ Code available' : '✗ Already in use');
    } catch {
      setCodeCheckMessage('');
    }
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    
    setSaving(true);
    setError(null);

    try {
      // Update project via single PATCH call
      await projectsApi.updateMetadata(project.id, {
        name: name.trim(),
        projectCode: canChangeCode ? projectCode.trim() || undefined : undefined,
        projectTitle: projectTitle.trim() || undefined,
        customer: customer.trim() || undefined,
        program: program.trim() || undefined,
        certificationAuthority: certAuthority,
        certificationBasis: certBasis.length > 0 ? certBasis : undefined,
      });

      // Fetch updated project
      const updated = await projectsApi.get(project.id);
      onSaved(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const toggleCertBasis = (basis: string) => {
    if (certBasis.includes(basis)) {
      setCertBasis(certBasis.filter(b => b !== basis));
    } else {
      setCertBasis([...certBasis, basis]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit Project</DialogTitle>
        </DialogHeader>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {error && (
            <div className="p-3 bg-destructive-light border border-destructive rounded-lg text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="edit-name">Project Name *</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Navigation Display System"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Short name for internal reference
            </p>
          </div>

          {/* Project Code */}
          <div className="space-y-2">
            <Label htmlFor="edit-code">Project Code</Label>
            <Input
              id="edit-code"
              value={projectCode}
              onChange={(e) => handleProjectCodeChange(e.target.value)}
              placeholder="e.g., NAV-100"
              maxLength={10}
              disabled={!canChangeCode}
              className={`uppercase ${!canChangeCode ? 'opacity-60 cursor-not-allowed' : ''}`}
            />
            {!canChangeCode && (
              <p className="text-xs text-warning">
                Cannot change code once documents are registered
              </p>
            )}
            {canChangeCode && codeCheckMessage && (
              <p className={`text-xs font-medium ${
                codeCheckMessage.includes('✓') ? 'text-success' : 'text-destructive'
              }`}>
                {codeCheckMessage}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Used in document numbers
            </p>
          </div>

          {/* Full Title */}
          <div className="space-y-2">
            <Label htmlFor="edit-title">Full Project Title</Label>
            <Input
              id="edit-title"
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              placeholder="e.g., Navigation Display System for Boeing 737"
            />
          </div>

          {/* Customer & Program */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-customer">Customer</Label>
              <Input
                id="edit-customer"
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder="e.g., Boeing"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-program">Program</Label>
              <Input
                id="edit-program"
                value={program}
                onChange={(e) => setProgram(e.target.value)}
                placeholder="e.g., 737 MAX"
              />
            </div>
          </div>

          {/* Certification Authority */}
          <div className="space-y-2">
            <Label htmlFor="edit-cert">Certification Authority</Label>
            <Select
              id="edit-cert"
              value={certAuthority}
              onChange={(e) => setCertAuthority(e.target.value)}
            >
              <option value="FAA">FAA (Federal Aviation Administration)</option>
              <option value="EASA">EASA (European Aviation Safety Agency)</option>
              <option value="TCCA">TCCA (Transport Canada)</option>
              <option value="Other">Other</option>
            </Select>
          </div>

          {/* Certification Basis */}
          <div className="space-y-2">
            <Label>Certification Basis</Label>
            <div className="space-y-2">
              {[
                { id: 'DO-178C', label: 'DO-178C (Software)' },
                { id: 'DO-254', label: 'DO-254 (Hardware)' },
                { id: 'ARP4754A', label: 'ARP4754A (Systems)' },
                { id: 'ARP4761', label: 'ARP4761 (Safety)' },
              ].map(({ id, label }) => (
                // eslint-disable-next-line no-restricted-syntax -- Native label/checkbox for certification checkboxes
                <label key={id} className="flex items-center gap-2 cursor-pointer">
                  {/* eslint-disable-next-line no-restricted-syntax -- Native checkbox for form */}
                  <input
                    type="checkbox"
                    checked={certBasis.includes(id)}
                    onChange={() => toggleCertBasis(id)}
                    className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Warning for projects with documents */}
          {hasDocuments && (
            <div className="p-3 bg-warning-light border border-warning rounded-lg">
              <p className="text-sm text-warning">
                <strong>Note:</strong> This project has {project.documents?.length} document(s). 
                Some fields cannot be changed.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || saving || (codeCheckMessage.includes('Already in use'))}
          >
            {saving && <Spinner size="sm" className="mr-2" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
